import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Compass } from "lucide-react";
import HorizonWaitlist from "@/components/alcott/HorizonWaitlist";
import { IMG } from "@/data/alcott";

const ease = [0.22, 1, 0.36, 1];

// Separate gate for The Horizon Circle. Membership is by invitation; unknown
// codes are routed to the waitlist (booking/auth is intentionally not wired yet).
export const HorizonGate = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [waitlist, setWaitlist] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Enter your membership code.");
    toast("Membership to The Horizon Circle is currently by invitation.", {
      description: "Join the waitlist and a curator will reach out.",
    });
    setWaitlist(true);
  };

  return (
    <div className="relative min-h-screen bg-[#F4EFE6] font-body text-[#0A0A0A]" data-testid="horizon-gate">
      <div className="absolute inset-0">
        <img src={IMG.horizon2} alt="" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4EFE6]/70 via-[#F4EFE6]/85 to-[#F4EFE6]" />
      </div>

      <Link to="/horizon" className="absolute left-6 top-6 z-10 inline-flex items-center gap-2 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#0A0A0A]/60 transition-colors hover:text-[#C05C42]" data-testid="horizon-gate-back">
        <ArrowLeft size={14} /> The Horizon Circle
      </Link>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease }}>
          <Compass size={30} strokeWidth={1.25} className="text-[#C05C42]" />
          <p className="mt-6 font-body text-[0.7rem] uppercase tracking-[0.28em] text-[#C05C42]">Member Access</p>
          <h1 className="mt-3 font-serif text-[clamp(2.4rem,7vw,4rem)] font-semibold leading-none tracking-tight">
            The Horizon Circle
          </h1>
          <p className="mt-5 font-body text-sm leading-relaxed text-[#0A0A0A]/60">
            Present your membership code to enter the members' sanctuary. Not yet a
            member? Request the waitlist — admission is by invitation.
          </p>

          <form onSubmit={submit} className="mt-9 space-y-4">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="MEMBERSHIP CODE"
              className="w-full rounded-xl border border-[#0A0A0A]/20 bg-[#FAF9F6] px-5 py-4 font-mono text-sm tracking-[0.15em] text-[#0A0A0A] outline-none placeholder:text-[#0A0A0A]/30 focus:border-[#C05C42]"
              data-testid="horizon-gate-code"
            />
            <button
              type="submit"
              className="w-full rounded-full bg-[#0A0A0A] py-4 font-body text-[0.7rem] uppercase tracking-[0.22em] text-[#FAF9F6] transition-transform duration-300 hover:-translate-y-0.5"
              data-testid="horizon-gate-submit"
            >
              Enter the Circle
            </button>
            <button
              type="button"
              onClick={() => setWaitlist(true)}
              className="w-full rounded-full border border-[#0A0A0A]/25 py-4 font-body text-[0.7rem] uppercase tracking-[0.22em] text-[#0A0A0A] transition-colors duration-300 hover:border-[#C05C42] hover:text-[#C05C42]"
              data-testid="horizon-gate-waitlist"
            >
              Request the waitlist
            </button>
          </form>

          <button onClick={() => navigate("/")} className="mt-8 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#0A0A0A]/45 hover:text-[#0A0A0A]" data-testid="horizon-gate-home">
            ← Return to Sybils & Society
          </button>
        </motion.div>
      </div>

      <HorizonWaitlist open={waitlist} onClose={() => setWaitlist(false)} context="The Horizon Circle — membership" />
    </div>
  );
};

export default HorizonGate;
